import { Button, IconButton } from '@mui/material';
import { SelectOption } from '@schooly/api';
import { TIMETABLE } from '@schooly/constants/test-ids';
import {
  CrossIcon,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalMain,
  ModalSmall,
  PublishIcon,
} from '@schooly/style';
import { FC, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form-lts';
import { useIntl } from 'react-intl';

import FormSelect2 from '../../../../components/ui/Input/FormSelect2';

type SelectSheetForm = { name: string };

interface SelectSheetModalProps {
  fileName: string;
  sheetNames: string[];
  onSelect: (sheetName: string) => void;
  onClose: () => void;
}

export const SelectSheetModal: FC<SelectSheetModalProps> = ({
  fileName,
  sheetNames,
  onSelect,
  onClose,
}) => {
  const { formatMessage } = useIntl();

  const form = useForm<SelectSheetForm>({
    defaultValues: { name: '' },
  });

  const options: SelectOption[] = useMemo(
    () => sheetNames.map((name, i) => ({ value: name, label: `${i + 1} - ${name}` })),
    [sheetNames],
  );

  return (
    <ModalSmall
      open
      onClose={onClose}
      PaperProps={{ 'data-test-id': TIMETABLE.IMPORT_SELECT_SHEET_MODAL }}
      sx={(theme) => ({
        [theme.breakpoints.down('lg')]: {
          alignItems: 'center',
        },
        '& .MuiDialog-paperFullWidth': {
          height: 'unset',
          [theme.breakpoints.down('lg')]: {
            width: 600,
          },
        },
      })}
    >
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(({ name }) => onSelect(name))}>
          <ModalHeader
            sx={{ alignItems: 'center' }}
            active
            multiline
            title={formatMessage(
              { id: 'school-timetables-Import-SelectSheetTitle' },
              { fileName: `"${fileName}"` },
            )}
          >
            <IconButton onClick={onClose}>
              <CrossIcon />
            </IconButton>
          </ModalHeader>

          <ModalMain>
            <ModalContent active>
              <FormSelect2
                name="name"
                labelTextId="school-timetables-Import-SelectPage"
                options={options}
                rules={{ required: true }}
              />
            </ModalContent>
          </ModalMain>

          <ModalFooter active sx={{ justifyContent: 'space-between' }}>
            <Button variant="outlined" onClick={onClose}>
              {formatMessage({ id: 'action-Cancel' })}
            </Button>
            <Button
              type="submit"
              startIcon={<PublishIcon />}
              sx={(theme) => ({
                '& .svg-icon path:last-child': { fill: theme.palette.primary.main },
              })}
            >
              {formatMessage({ id: 'school-timetables-Import' })}
            </Button>
          </ModalFooter>
        </form>
      </FormProvider>
    </ModalSmall>
  );
};
